/**
 * useRaceHistory Hook
 *
 * Fetches and manages the user's race history from the database.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { RaceRoom, RaceParticipant } from '@/types/race';

export interface RaceHistoryEntry extends RaceRoom {
  participant_count: number;
}

interface UseRaceHistoryReturn {
  races: RaceHistoryEntry[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useRaceHistory(): UseRaceHistoryReturn {
  const { user } = useAuth();
  const [races, setRaces] = useState<RaceHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRaces = useCallback(async () => {
    if (!user) {
      setRaces([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch races created by this user
      const { data: roomsData, error: roomsError } = await supabase
        .schema('race')
        .from('rooms')
        .select('*')
        .eq('created_by', user.id)
        .order('created_at', { ascending: false });

      if (roomsError) {
        throw roomsError;
      }

      if (!roomsData || roomsData.length === 0) {
        setRaces([]);
        setLoading(false);
        return;
      }

      // Fetch participant counts for each race
      const roomIds = roomsData.map((r) => r.id);
      const { data: participantCounts, error: countError } = await supabase
        .schema('race')
        .from('participants')
        .select('race_room_id')
        .in('race_room_id', roomIds);

      if (countError) {
        console.error('Error fetching participant counts:', countError);
      }

      // Count participants per room
      const countMap = new Map<string, number>();
      for (const p of participantCounts || []) {
        const count = countMap.get(p.race_room_id) || 0;
        countMap.set(p.race_room_id, count + 1);
      }

      // Merge counts with rooms
      const racesWithCounts: RaceHistoryEntry[] = roomsData.map((room) => ({
        ...room,
        participant_count: countMap.get(room.id) || 0,
      }));

      setRaces(racesWithCounts);
    } catch (err) {
      console.error('Error fetching race history:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch race history');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchRaces();
  }, [fetchRaces]);

  return {
    races,
    loading,
    error,
    refresh: fetchRaces,
  };
}

/**
 * Delete a race from the database
 * Note: CASCADE delete will remove participants, progress, and live_stats
 */
export async function deleteRace(raceId: string): Promise<{
  success: boolean;
  error: string | null;
}> {
  try {
    const { error } = await supabase
      .schema('race')
      .from('rooms')
      .delete()
      .eq('id', raceId);

    if (error) {
      throw error;
    }

    return { success: true, error: null };
  } catch (err) {
    console.error('Error deleting race:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to delete race',
    };
  }
}

export interface RaceLiveStatsRow {
  user_id: string;
  cards_completed: number;
  correct_count: number;
  incorrect_count: number;
  accuracy: number;
  total_time: number;
}

/**
 * Fetch detailed stats for a specific race
 */
export async function fetchRaceStats(raceId: string): Promise<{
  room: RaceRoom | null;
  participants: RaceParticipant[];
  liveStats: RaceLiveStatsRow[];
  error: string | null;
}> {
  try {
    // Fetch room details
    const { data: roomData, error: roomError } = await supabase
      .schema('race')
      .from('rooms')
      .select('*')
      .eq('id', raceId)
      .single();

    if (roomError) {
      throw roomError;
    }

    // Fetch participants with their final stats
    const { data: participantsData, error: participantsError } = await supabase
      .schema('race')
      .from('participants')
      .select('*')
      .eq('race_room_id', raceId)
      .order('total_cards', { ascending: false });

    if (participantsError) {
      throw participantsError;
    }

    // Fetch live_stats for accurate stats (correct_count, incorrect_count, total_time)
    const { data: liveStatsData, error: liveStatsError } = await supabase
      .schema('race')
      .from('live_stats')
      .select('user_id, cards_completed, correct_count, incorrect_count, accuracy, total_time')
      .eq('race_room_id', raceId);

    if (liveStatsError) {
      console.error('Error fetching live stats:', liveStatsError);
    }

    return {
      room: roomData,
      participants: participantsData || [],
      liveStats: liveStatsData || [],
      error: null,
    };
  } catch (err) {
    console.error('Error fetching race stats:', err);
    return {
      room: null,
      participants: [],
      liveStats: [],
      error: err instanceof Error ? err.message : 'Failed to fetch race stats',
    };
  }
}
